import React from 'react';
import { Table } from 'reactstrap';
import { SkeletonLoader } from '../SkeletonLoader';
import styles from './DataTable.module.css';

export interface TableColumn {
  key: string;
  label: string | React.ReactNode;
  render?: (value: any, row: any) => React.ReactNode;
  width?: string;
  className?: string;
}

interface DataTableProps {
  columns: TableColumn[];
  data: any[];
  loading: boolean;
  emptyState?: React.ReactNode;
  skeletonRows?: number;
  expandableRows?: boolean;
  expandedRowRender?: (row: any) => React.ReactNode;
  expandedRows?: Set<string | number>;
  onRowToggle?: (rowId: string | number) => void;
  getRowClassName?: (row: any) => string;
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  data,
  loading,
  emptyState,
  skeletonRows = 5,
  expandableRows = false,
  expandedRowRender,
  expandedRows = new Set(),
  onRowToggle,
  getRowClassName
}) => {
  const renderSkeletonRows = () => Array.from({ length: skeletonRows }, (_, index) => (
      <tr key={`skeleton-${index}`}>
        {expandableRows && (
          <td className="py-3" style={{ width: '40px' }}>
            <SkeletonLoader width="20px" height="1rem" aria-label="Loading expand button" />
          </td>
        )}
        {columns.map((column) => {
          const isFirstColumn = column.key === columns[0]?.key;
          const isLastColumn = column.key === columns[columns.length - 1]?.key;
          let width = '90%';
          if (isFirstColumn) width = '80%';
          else if (isLastColumn) width = '70%';

          return (
            <td key={`skeleton-${index}-${column.key}`} className="py-3" style={column.width ? { width: column.width, minWidth: column.width } : undefined}>
              <SkeletonLoader width={width} height="1rem" aria-label="Loading content" />
            </td>
          );
        })}
      </tr>
    ));

  const renderDataRows = () => data.map((row, index) => {
      const rowId = row.id || index;
      const isExpanded = expandedRows.has(rowId);
      const rowClassName = getRowClassName ? getRowClassName(row) : '';
      const combinedClassName = [
        isExpanded ? styles.expandedMainRow : '',
        rowClassName
      ].filter(Boolean).join(' ');

      return (
        <React.Fragment key={rowId}>
          <tr className={combinedClassName}>
            {expandableRows && (
              <td style={{ width: '40px', textAlign: 'center' }}>
                <button
                  type="button"
                  className={styles.expandButton}
                  onClick={() => onRowToggle?.(rowId)}
                  aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                >
                  <i
                    className={`fas fa-chevron-right ${styles.expandIcon} ${isExpanded ? styles.expandIconExpanded : ''}`}
                  />
                </button>
              </td>
            )}
            {columns.map((column) => (
              <td
                key={`${rowId}-${column.key}`}
                style={column.width ? { width: column.width, minWidth: column.width } : undefined}
                className={column.className}
              >
                {column.render ? column.render(row[column.key], row) : row[column.key]}
              </td>
            ))}
          </tr>
          {expandableRows && isExpanded && expandedRowRender && (
            <tr className={`${styles.expandableRow} ${styles.expandableRowExpanded}`}>
              <td colSpan={columns.length + 1} className={styles.expandableRowCell} style={{ padding: 0 }}>
                <div className={`${styles.expandableRowContent} ${styles.expandableRowContentExpanded}`}>
                  {expandedRowRender(row)}
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });

  const renderEmptyRow = () => (
      <tr>
        <td colSpan={columns.length + (expandableRows ? 1 : 0)}>
          {emptyState || <div className="text-center py-4">No data available</div>}
        </td>
      </tr>
    );

  return (
    <Table className={`align-items-center table-flush ${expandableRows ? styles.tableStable : ''}`}>
      <thead className="thead-light">
        <tr>
          {expandableRows && (
            <th scope="col" style={{ width: '40px' }} aria-label="Expand column" />
          )}
          {columns.map((column) => (
            <th key={column.key} scope="col" style={column.width ? { width: column.width, minWidth: column.width } : undefined}>
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading ? renderSkeletonRows() : data.length > 0 ? renderDataRows() : renderEmptyRow()}
      </tbody>
    </Table>
  );
};

export default DataTable;